import React, { useState } from "react";
import { Instagram, ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/motion";

/**
 * Instagram module — tabbed regional selector.
 * Each entry is { region, handle, widgetUrl }.
 * widgetUrl is a LightWidget / SnapWidget iframe URL (e.g. https://cdn.lightwidget.com/widgets/xxx.html).
 * If widgetUrl is blank, a beautifully styled "Follow on Instagram" CTA card is shown instead.
 */
const InstagramFeed = ({ entries = [], brandName = "" }) => {
  const list = entries.filter(Boolean);
  const [active, setActive] = useState(0);
  if (list.length === 0) return null;
  const current = list[active] || list[0];
  const profileUrl = `https://www.instagram.com/${current.handle}/`;

  return (
    <section data-testid={`instagram-feed-${brandName}`} className="bg-background">
      <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
        <FadeUp>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <p className="kicker mb-3">On Instagram</p>
              <h2 className="headline text-4xl md:text-6xl uppercase text-ink leading-[1.05]">
                Live from the feed
              </h2>
            </div>
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-primary"
              className="inline-flex items-center gap-2 font-sans text-sm font-bold bg-ink text-white rounded-full pl-6 pr-5 py-3.5 hover:bg-primary transition-colors self-start"
            >
              <Instagram size={16} /> Follow @{current.handle} <ArrowUpRight size={16} />
            </a>
          </div>

          {list.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-8" data-testid="instagram-region-tabs">
              {list.map((e, i) => (
                <button
                  key={e.handle}
                  type="button"
                  onClick={() => setActive(i)}
                  data-testid={`instagram-tab-${e.region.toLowerCase()}`}
                  className={`font-mono text-xs font-extrabold uppercase tracking-widest rounded-full px-4 py-2 border-2 transition-colors ${
                    i === active
                      ? "bg-ink text-white border-ink"
                      : "bg-transparent text-ink border-ink/15 hover:border-ink"
                  }`}
                >
                  {e.region} · @{e.handle}
                </button>
              ))}
            </div>
          )}
        </FadeUp>

        <FadeUp>
          {current.widgetUrl ? (
            <div
              className="rounded-2xl overflow-hidden border border-border bg-secondary"
              data-testid="instagram-widget"
            >
              <iframe
                src={current.widgetUrl}
                title={`@${current.handle} on Instagram`}
                allowtransparency="true"
                allow="encrypted-media"
                className="w-full block"
                style={{ border: 0, minHeight: "540px" }}
                scrolling="no"
                loading="lazy"
              />
            </div>
          ) : (
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-fallback"
              className="group block border-2 border-dashed border-ink/15 hover:border-primary rounded-2xl bg-secondary p-10 md:p-16 text-center transition-colors"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-ink text-white group-hover:bg-primary transition-colors mb-6">
                <Instagram size={28} />
              </div>
              <p className="headline text-3xl md:text-4xl uppercase text-ink">
                @{current.handle}
              </p>
              <p className="mt-4 text-base md:text-lg text-foreground/70 max-w-xl mx-auto leading-relaxed">
                Follow us on Instagram for daily school news, admissions insights and behind-the-scenes from our editorial team.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-sans text-sm font-bold text-primary group-hover:text-ink transition-colors">
                Open profile <ArrowUpRight size={16} />
              </span>
            </a>
          )}
        </FadeUp>
      </div>
    </section>
  );
};

export default InstagramFeed;
